import React, { useState } from 'react';

const QuestionCard = ({ 
  title, 
  subtitle, 
  examples, 
  inputType, 
  options, 
  value, 
  onChange, 
  customOption,
  customValue,
  onCustomChange,
  categoryIcon,
  categoryName,
  questionNumber,
  totalQuestions
}) => {
  const [focused, setFocused] = useState(false);
  const [customInputFocused, setCustomInputFocused] = useState(false);
  
  // Handle checkbox change
  const handleCheckboxChange = (option, isChecked) => {
    let newValues;
    if (isChecked) {
      // Add the option to the array if it's checked
      newValues = Array.isArray(value) ? [...value, option] : [option];
    } else {
      // Remove the option from the array if it's unchecked
      newValues = Array.isArray(value) ? value.filter(item => item !== option) : [];
    }
    onChange(newValues);
  };
  
  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 animate-fadeIn">
      {/* Category Badge */}
      <div className="flex items-center mb-5">
        <div className="flex items-center bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-2 rounded-full border border-blue-100">
          <span className="text-lg mr-2">{categoryIcon}</span>
          <span className="text-blue-700 font-medium">{categoryName}</span>
        </div>
        <div className="h-px bg-gray-200 flex-grow mx-4"></div>
        <div className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full flex items-center justify-center font-medium text-sm">
          {questionNumber} / {totalQuestions}
        </div>
      </div>
      
      <h2 className="text-xl font-semibold mb-3 text-gray-800">{title}</h2>
      {subtitle && <p className="text-gray-500 text-sm mb-6">{subtitle}</p>}
      {examples && <p className="text-gray-500 text-sm mb-6">{examples}</p>}
      
      {inputType === 'textarea' ? (
        <div className="relative">
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder="请在此输入..."
            rows={5}
            className={`w-full bg-gray-50 border ${focused ? 'border-blue-400' : 'border-gray-200'} rounded-lg p-4 text-gray-800 placeholder-gray-400 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400/40`}
          />
          <div className={`absolute bottom-2 right-3 text-xs text-gray-400 transition-opacity duration-300 ${value?.length > 0 ? 'opacity-100' : 'opacity-0'}`}>
            {value?.length} 字符
          </div>
        </div>
      ) : inputType === 'radio' ? (
        <div className="space-y-4 my-4">
          {options.map((option, index) => (
            <label key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group">
              <div className="relative flex items-center justify-center mt-0.5">
                <input
                  type="radio"
                  checked={value === option}
                  onChange={() => onChange(option)}
                  className="appearance-none"
                />
                <div className={`w-5 h-5 rounded-full border-2 ${value === option ? 'border-blue-500' : 'border-gray-300 group-hover:border-gray-400'} transition-colors duration-200`}></div>
                {value === option && (
                  <div className="absolute w-3 h-3 rounded-full bg-blue-500 animate-scaleIn"></div>
                )}
              </div>
              <span className="text-gray-700">{option}</span>
            </label>
          ))}
          
          {customOption && (
            <div className="mt-4">
                <label className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group">
                <div className="relative flex items-center justify-center mt-1.5">
                    <input
                    type="radio"
                    checked={!options.includes(value) && value !== '' && value === customValue && customValue.trim() !== ''}
                    onChange={() => {
                        // Only set the value if there's actual content in the custom field
                        if (customValue && customValue.trim() !== '') {
                        onChange(customValue);
                        }
                    }}
                    className="appearance-none"
                    />
                    <div className={`w-5 h-5 rounded-full border-2 ${!options.includes(value) && value !== '' && value === customValue && customValue.trim() !== '' ? 'border-blue-500' : 'border-gray-300 group-hover:border-gray-400'} transition-colors duration-200`}></div>
                    {!options.includes(value) && value !== '' && value === customValue && customValue.trim() !== '' && (
                    <div className="absolute w-3 h-3 rounded-full bg-blue-500 animate-scaleIn"></div>
                    )}
                </div>
                <div className="flex-1">
                    <span className="text-gray-700 block mb-2">Other:</span>
                    <input
                    type="text"
                    value={customValue || ''}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        onCustomChange(newValue);
                        
                        // If the custom option is already selected, update the main value
                        if (!options.includes(value) && (value === customValue || value === 'Other' || value === '')) {
                        if (newValue.trim() !== '') {
                            onChange(newValue);
                        } else {
                            onChange(''); // Clear the selection if input is empty
                        }
                        }
                    }}
                    onFocus={() => setCustomInputFocused(true)}
                    onBlur={() => setCustomInputFocused(false)}
                    onClick={() => {
                        // Only select this option if there's text
                        if (customValue && customValue.trim() !== '') {
                        onChange(customValue);
                        }
                    }}
                    placeholder="请输入其他选项..."
                    className={`w-full bg-gray-50 border ${customInputFocused ? 'border-blue-400' : 'border-gray-200'} rounded-lg p-2 text-gray-800 placeholder-gray-400 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400/40`}
                    />
                </div>
                </label>
            </div>
          )}
        </div>
      ) : inputType === 'checkbox' ? (
        <div className="space-y-4 my-4">
          {options.map((option, index) => (
            <label key={index} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group">
              <div className="relative flex items-center justify-center mt-0.5">
                <input
                  type="checkbox"
                  checked={Array.isArray(value) && value.includes(option)}
                  onChange={(e) => handleCheckboxChange(option, e.target.checked)}
                  className="appearance-none"
                />
                <div className={`w-5 h-5 rounded-md border-2 ${Array.isArray(value) && value.includes(option) ? 'border-blue-500 bg-blue-500' : 'border-gray-300 group-hover:border-gray-400'} transition-colors duration-200`}></div>
                {Array.isArray(value) && value.includes(option) && (
                  <div className="absolute text-white">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
              <span className="text-gray-700">{option}</span>
            </label>
          ))}
          
          {customOption && (
            <div className="mt-4">
                <label className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 cursor-pointer group">
                <div className="relative flex items-center justify-center mt-1.5">
                    <input
                    type="checkbox"
                    checked={customValue && customValue.trim() !== '' && Array.isArray(value) && value.includes(customValue)}
                    onChange={(e) => {
                        if (e.target.checked && customValue && customValue.trim() !== '') {
                        // Only add to selections if there's actual text content
                        const newValues = Array.isArray(value) ? 
                            (value.includes(customValue) ? value : [...value, customValue]) : 
                            [customValue];
                        onChange(newValues);
                        } else {
                        // Remove from selections
                        const newValues = Array.isArray(value) ? 
                            value.filter(item => item !== customValue && item !== 'Other') : 
                            [];
                        onChange(newValues);
                        }
                    }}
                    className="appearance-none"
                    />
                    <div className={`w-5 h-5 rounded-md border-2 ${customValue && customValue.trim() !== '' && Array.isArray(value) && value.includes(customValue) ? 'border-blue-500 bg-blue-500' : 'border-gray-300 group-hover:border-gray-400'} transition-colors duration-200`}></div>
                    {customValue && customValue.trim() !== '' && Array.isArray(value) && value.includes(customValue) && (
                    <div className="absolute text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                    </div>
                    )}
                </div>
                <div className="flex-1">
                    <span className="text-gray-700 block mb-2">Other:</span>
                    <input
                    type="text"
                    value={customValue || ''}
                    onChange={(e) => {
                        const newValue = e.target.value;
                        onCustomChange(newValue);
                        
                        // Update the selection array when text is entered or cleared
                        if (Array.isArray(value)) {
                        // First remove any existing custom value or "Other" placeholder
                        const filteredValues = value.filter(item => item !== customValue && item !== 'Other');
                        
                        // Only add the new value if it's not empty
                        if (newValue.trim() !== '') {
                            const newValues = [...filteredValues, newValue];
                            onChange(newValues);
                        } else {
                            // If the input is empty, just update with the filtered values
                            onChange(filteredValues);
                        }
                        }
                    }}
                    onFocus={() => setCustomInputFocused(true)}
                    onBlur={() => setCustomInputFocused(false)}
                    placeholder="请输入其他选项..."
                    className={`w-full bg-gray-50 border ${customInputFocused ? 'border-blue-400' : 'border-gray-200'} rounded-lg p-2 text-gray-800 placeholder-gray-400 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-400/40`}
                    />
                </div>
                </label>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default QuestionCard;